import { useState } from "react";
import { useToast } from "@/components/base/Toast";
import type { QuotationLineItem } from "@/mocks/quotationsData";
import { fetchApi } from "@/services/apiService";

type Props = {
  open: boolean;
  onClose: () => void;
  items: QuotationLineItem[];
  createdBy?: string;
  onSaved?: () => void;
};

export function SaveAsTemplateModal({ open, onClose, items, createdBy = "Admin", onSaved }: Props) {
  const { showToast } = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      showToast("Please enter a template name", "error");
      return;
    }
    setSaving(true);
    try {
      await fetchApi("/quotations/templates", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          created_by: createdBy,
          items,
        }),
      });
      showToast("Template saved successfully", "success");
      setName("");
      setDescription("");
      if (onSaved) onSaved();
      onClose();
    } catch (err: any) {
      showToast(err.message || "Failed to save template", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-xl border border-gray-200 shadow-lg max-w-sm w-full z-10">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Save as Template</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Template Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. 2BHK Standard Package"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this template..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
              />
            </div>

            <div className="text-xs text-gray-400">
              <i className="ri-information-line mr-1" />
              {items.length} line items will be saved in this template
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              {saving && <i className="ri-loader-4-line animate-spin text-sm" />}
              Save Template
            </button>
          </div>
        </div>
      </div>
    </>
  );
}